/**
 * MessageRouter - Concentrates all protocol-to-state mapping in one place.
 *
 * Maps (kind, type) tuples to handler functions that return state mutations.
 * This is the single source of truth for how protocol messages affect app state.
 *
 * Benefits:
 * - Adding a new event type = adding one entry to the registry
 * - Protocol changes are isolated to this file
 * - Handlers are testable in isolation
 * - Clear visibility into the full protocol surface area
 */

import {
  Kind,
  EvtType,
  ProfileEvtType,
  LocalVersionEvtType,
  VersionEvtType,
  UploadEvtType,
  CustomHostnameEvtType,
  type Msg,
  type SnapshotStartPayload,
  type SnapshotChunkPayload,
  type SnapshotEndPayload,
} from "@interlucent/organization-protocol";
import { logger } from '../log';

const log = logger('router');

import {
  type StateMutation,
  type StoreState,
  // Snapshot handlers
  handleSnapshotStart,
  handleSnapshotChunk,
  handleSnapshotEnd,
  // Project handlers
  handleProjectUpsert,
  handleProjectDeleted,
  // Profile handlers
  handleProfileUpsert,
  handleProfileCreated,
  handleProfileDeleted,
  handleProfileActivated,
  handleProfileDeactivated,
  // Application handlers
  handleApplicationCreated,
  handleApplicationDeleted,
  handleApplicationUpdated,
  handleApplicationMoved,
  // Version handlers
  handleVersionCreated,
  handleVersionDeleted,
  handleVersionUpdated,
  // Local version handlers
  handleLocalVersionCreated,
  handleLocalVersionUpdated,
  handleLocalVersionDeleted,
  handleLocalVersionTokensCycled,
  // Upload handlers
  handleUploadProgress,
  handleUploadAborted,
  handleUploadCompleted,
  handleUploadPaused,
  handleUploadResumed,
  handleUploadInterrupted,
  // Custom hostname handlers
  handleCustomHostnameCreated,
  handleCustomHostnameUpdated,
  handleCustomHostnameDeleted,
  // Pool coverage handler
  handleAppPoolCoverageChanged,
} from "./handlers";

// =============================================================================
// HANDLER REGISTRY
// =============================================================================

type EventHandler = (payload: any) => StateMutation;

/**
 * Registry of event handlers keyed by event type.
 * This is the single source of truth for protocol-to-state mapping.
 */
const eventHandlers: Record<string, EventHandler> = {
  // Project events
  [EvtType.ProjectCreated]: handleProjectUpsert,
  [EvtType.ProjectUpdated]: handleProjectUpsert,
  [EvtType.ProjectDeleted]: handleProjectDeleted,

  // Profile events (via EvtType)
  [EvtType.ProfileUpdated]: handleProfileUpsert,

  // Profile lifecycle events (via ProfileEvtType)
  [ProfileEvtType.ProfileCreated]: handleProfileCreated,
  [ProfileEvtType.ProfileDeleted]: handleProfileDeleted,
  [ProfileEvtType.ProfileActivated]: handleProfileActivated,
  [ProfileEvtType.ProfileDeactivated]: handleProfileDeactivated,

  // Profile mapping events — informational only; membership truth arrives
  // via AppPoolCoverageChanged after the pool re-materializes.
  [ProfileEvtType.ProfileApplicationMapped]: () => ({}),
  [ProfileEvtType.ProfileApplicationUnmapped]: () => ({}),
  [ProfileEvtType.ProfileApplicationsBatchMapped]: () => ({}),

  // Application events
  "application.created": handleApplicationCreated,
  [EvtType.ApplicationDeleted]: handleApplicationDeleted,
  [EvtType.ApplicationUpdated]: handleApplicationUpdated,
  [EvtType.ApplicationMoved]: handleApplicationMoved,

  // Version events
  "version.created": handleVersionCreated,
  "version.deleted": handleVersionDeleted,
  [VersionEvtType.Updated]: handleVersionUpdated,

  // Local version events
  [LocalVersionEvtType.Created]: handleLocalVersionCreated,
  [LocalVersionEvtType.Updated]: handleLocalVersionUpdated,
  [LocalVersionEvtType.Deleted]: handleLocalVersionDeleted,
  [LocalVersionEvtType.TokensCycled]: handleLocalVersionTokensCycled,

  // Upload events
  [UploadEvtType.UploadProgress]: handleUploadProgress,
  [UploadEvtType.UploadAborted]: handleUploadAborted,
  [UploadEvtType.UploadCompleted]: handleUploadCompleted,
  [UploadEvtType.UploadPaused]: handleUploadPaused,
  [UploadEvtType.UploadResumed]: handleUploadResumed,
  [UploadEvtType.UploadInterrupted]: handleUploadInterrupted,

  // Custom hostname events
  [CustomHostnameEvtType.Created]: handleCustomHostnameCreated,
  [CustomHostnameEvtType.Updated]: handleCustomHostnameUpdated,
  [CustomHostnameEvtType.Deleted]: handleCustomHostnameDeleted,

  // Pool membership coverage — fires when materializePool commits for
  // pools whose resolved app-set changed. Payload: `{ coverageByAppId:
  // { appId → AppPoolCoverageEntry[] } }`. Empty arrays mean the app
  // lost all coverage (drop from local map).
  [EvtType.AppPoolCoverageChanged]: handleAppPoolCoverageChanged,
};

// =============================================================================
// ROUTER
// =============================================================================

export type RouteResult = {
  mutation: StateMutation | null;
  updateSeq: boolean;
};

/**
 * Route a protocol message to the appropriate handler.
 *
 * @param msg - The protocol message to route
 * @returns RouteResult with mutation (if any) and whether to update lastSeq
 */
export function routeMessage(msg: Msg): RouteResult {
  log.debug(`Routing message: ${msg.kind} ${msg.type} seq: ${msg.seq}`);

  switch (msg.kind) {
    // =========================================================================
    // SNAPSHOT MESSAGES
    // =========================================================================
    case Kind.SnapshotStart: {
      return {
        mutation: handleSnapshotStart(msg.payload as SnapshotStartPayload),
        updateSeq: false,
      };
    }

    case Kind.SnapshotChunk: {
      return {
        mutation: handleSnapshotChunk(msg.payload as SnapshotChunkPayload),
        updateSeq: false,
      };
    }

    case Kind.SnapshotEnd: {
      return {
        mutation: handleSnapshotEnd(msg.payload as SnapshotEndPayload),
        updateSeq: false, // handleSnapshotEnd sets lastSeq directly
      };
    }

    // =========================================================================
    // EVENT MESSAGES
    // =========================================================================
    case Kind.Evt: {
      const type = msg.type;
      if (!type) {
        log.warn('Event message missing type');
        return { mutation: null, updateSeq: true };
      }

      const handler = eventHandlers[type];
      if (handler) {
        return {
          mutation: handler(msg.payload),
          updateSeq: true,
        };
      }

      log.debug(`Unhandled event type: ${type}`, msg.payload);
      return { mutation: null, updateSeq: true };
    }

    // =========================================================================
    // OTHER MESSAGE KINDS (no state mutation)
    // =========================================================================
    case Kind.Ack:
    case Kind.Err:
    case Kind.Hello:
    case Kind.Tick:
    case Kind.Pong:
    case Kind.Cmd:
    default: {
      return { mutation: null, updateSeq: true };
    }
  }
}

/**
 * Apply a routed message to the store state.
 *
 * @param msg - The protocol message
 * @param currentState - Current store state
 * @param set - Zustand set function
 */
export function applyMessage(
  msg: Msg,
  get: () => StoreState,
  set: (partial: Partial<StoreState> | ((s: StoreState) => Partial<StoreState>)) => void
): void {
  const currentState = get();

  // Skip old messages
  if (msg.seq && msg.seq <= currentState.lastSeq) {
    log.debug('Skipping message - seq too old');
    return;
  }

  const { mutation, updateSeq } = routeMessage(msg);

  if (mutation) {
    if (typeof mutation === 'function') {
      set(mutation);
    } else {
      set(mutation);
    }
  }

  if (updateSeq && msg.seq) {
    set({ lastSeq: msg.seq });
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export { eventHandlers };
export type { StateMutation, StoreState };
